import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AppLayout } from '@/components/AppLayout';
import { DateRangeFilter } from '@/components/reports/DateRangeFilter';
import { ExportButton } from '@/components/reports/ExportButton';
import { MetricCard } from '@/components/reports/MetricCard';
import { RecruiterComparisonChart } from '@/components/reports/RecruiterComparisonChart';
import { useRecruitersReport, useRecruiterDetail } from '@/hooks/useReports';

function getDefaultDates() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
}

export default function RecruiterReportPage() {
  const { user } = useAuth();
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const isAdminOrOwner = user?.role === 'owner' || user?.role === 'admin';

  // RECRUITER vê apenas suas próprias métricas
  const effectiveUserId = isAdminOrOwner ? selectedUserId : user?.userId || null;

  const { data: recruiters, isLoading, error } = useRecruitersReport(startDate, endDate);
  const { data: detail } = useRecruiterDetail(effectiveUserId || '', startDate, endDate);

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Relatório de Recrutadores</h1>
          <div className="flex items-center gap-3">
            <DateRangeFilter startDate={startDate} endDate={endDate} onChange={(s, e) => { setStartDate(s); setEndDate(e); }} />
            <ExportButton reportType="recruiter" startDate={startDate} endDate={endDate} userId={effectiveUserId || undefined} />
          </div>
        </div>

        {isLoading && <p className="text-gray-500">Carregando relatório...</p>}
        {error && <p className="text-red-600">Erro ao carregar: {error.message}</p>}

        {isAdminOrOwner && recruiters && (
          <>
            <RecruiterComparisonChart data={recruiters} />

            <div className="bg-white rounded-lg border mt-6 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Recrutador</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600">Vagas</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600">Candidatos</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600">Aprovação</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600">Tempo Médio</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {recruiters.map((r) => (
                    <tr key={r.userId} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-900">{r.name}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{r.totalJobs}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{r.totalCandidates}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{r.approvalRate.toFixed(1)}%</td>
                      <td className="px-4 py-2 text-right text-gray-700">{r.averageHiringTime.toFixed(1)} dias</td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => setSelectedUserId(r.userId === selectedUserId ? null : r.userId)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {r.userId === selectedUserId ? 'Fechar' : 'Detalhar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {detail && (
          <div className="mt-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Detalhamento — {detail.name}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <MetricCard label="Vagas" value={detail.totalJobs} />
              <MetricCard label="Candidatos" value={detail.totalCandidates} />
              <MetricCard label="Aprovação" value={`${detail.approvalRate.toFixed(1)}%`} />
              <MetricCard label="Tempo Médio" value={detail.averageHiringTime.toFixed(1)} suffix="dias" />
            </div>
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Vaga</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600">Candidatos</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.jobs.map((j) => (
                    <tr key={j.jobId} className="border-t">
                      <td className="px-4 py-2 text-gray-900">{j.title}</td>
                      <td className="px-4 py-2 text-gray-700">{j.status}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{j.candidateCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!isAdminOrOwner && !detail && !isLoading && (
          <p className="text-gray-500">Carregando suas métricas...</p>
        )}
      </div>
    </AppLayout>
  );
}
